import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';
import { DatePipe } from '@angular/common';
import { TextUtil } from "@qid/core";
import { Field } from '@qid/field';
import { Share } from "../model/Share";
import { ExportSharesUtil } from "../utils/export-shares.util";
import { ParticipantRoles } from "../enum/participant-roles.enum";
import { CollectionTemplate } from "../../collection-template";


@Injectable({
  providedIn: 'root'
})
export class ExportExcelService {

  constructor(private datePipe: DatePipe) {
  }


  public async exportExcel(
    shares: Share[], selectedTemplate: CollectionTemplate = null
  ) {

    const workbook = new ExcelJS.Workbook();
    const orderedShares = ExportExcelService.sortSharesByForm(shares);

    // one sheet per form; single form keeps name "Data"
    const byForm = new Map<string, Share[]>();
    for (const share of orderedShares) {
      const key = ExportExcelService.getFormSortKey(share);
      if (!byForm.has(key)) byForm.set(key, []);
      byForm.get(key).push(share);
    }
    const groups = byForm.size ? [...byForm.values()] : [[]];

    groups.forEach((groupShares, i) => {
      const sheetName = groups.length === 1
        ? 'Data'
        // ponytail: purpose truncated; rare name clash → Form2 ok
        : ((groupShares[0]?.template?.purpose || `Form${i + 1}`).replace(/[\\/?*[\]]/g, '').slice(0, 31) || `Form${i + 1}`);
      const sheet = workbook.addWorksheet(sheetName);

      sheet.columns = ExportExcelService.getHeaderColumns(groupShares, selectedTemplate);

      for (const index in groupShares) {
        const serialNo = Number(index) + 1;
        this.addRowForEachParticipant(serialNo, groupShares[index], selectedTemplate, sheet)
        sheet.getRow(+index).alignment = { wrapText: true }
      }

      sheet.getRow(1).font = { bold: true };
      sheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
      sheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFBFBFBF' },
        bgColor: { argb: 'FFBFBFBF' },
      };
    });

    await ExportSharesUtil.downloadFile(workbook);
  }

  private static getHeaderColumns(
    shares: Share[],
    template: CollectionTemplate = null
  ) {
    let columns: any[] = [
      { header: 'Serial', key: 'serial', width: 5 },
      { header: 'Date', key: 'date', width: 10 },
      { header: 'Time', key: 'time', width: 10 },
      { header: 'Name', key: 'senderName', width: 20 },
      { header: 'Email', key: 'senderEmail', width: 20 },
      { header: 'Phone Number', key: 'senderPhoneNumber', width: 20 },
      { header: 'Identifier', key: 'webhookIdentifier', width: 20 },
      { header: 'Document', key: 'idName', width: 20 },
      { header: 'Form', key: 'templateName', width: 20 },
    ];

    // Check if any share is of type 'check-in' and add checkout date column
    const hasCheckInShares = shares.some(share => share?.template?.type === 'check-in');
    if (hasCheckInShares) {
      columns.push({
        header: 'Check Out Date',
        key: 'checkOutDate',
        width: 30,
      });
    }

    if (!template || !template.otherInfo) {
      for (const { header, key } of ExportExcelService.collectOtherFieldColumns(shares)) {
        columns.push({
          header,
          key,
          width: 20,
          style: { alignment: { wrapText: true } },
        });
      }

      return columns;
    }

    for (let info of template.otherInfo) {
      if (!ExportExcelService.isExportableOtherField(info)) {
        continue;
      }
      const header = ExportExcelService.getFieldHeader(info);
      columns.push({
        header: header,
        key: TextUtil.removeAllSpaces(header),
        width: 20,
        style: { alignment: { wrapText: true } },
      });
    }

    return columns;
  }

  /** Stable sort: all rows for one form (template) together, then by submission time within the form. */
  private static sortSharesByForm(shares: Share[]): Share[] {
    return [...shares].sort((a, b) => {
      const keyA = ExportExcelService.getFormSortKey(a);
      const keyB = ExportExcelService.getFormSortKey(b);
      const byForm = keyA.localeCompare(keyB, undefined, { sensitivity: 'base' });
      if (byForm !== 0) {
        return byForm;
      }
      const timeA = new Date(a?.createTime as Date | string | number).getTime();
      const timeB = new Date(b?.createTime as Date | string | number).getTime();
      return (Number.isNaN(timeA) ? 0 : timeA) - (Number.isNaN(timeB) ? 0 : timeB);
    });
  }

  private static getFormSortKey(share: Share): string {
    const t = share?.template;
    if (!t) {
      return '';
    }
    const id = t._id != null ? String(t._id) : '';
    if (id) {
      return id;
    }
    return (t.purpose ?? '').trim();
  }

  private static isExportableOtherField(field: Field): boolean {
    return !field?.sectionBreak;
  }

  private static getFieldHeader(field: Field): string {
    let header = field.label ? field.label : field.value;
    if (!header) {
      header = field.name || 'Field';
    }
    return header;
  }

  private static collectOtherFieldColumns(
    shares: Share[]
  ): { header: string; key: string }[] {
    const seenKeys = new Set<string>();
    const columns: { header: string; key: string }[] = [];

    for (const share of shares) {
      const fields = [
        ...(share?.otherFields ?? []),
        ...(share?.participants ?? [])
          .filter(p => p.roles?.indexOf(ParticipantRoles.organiser) === -1)
          .flatMap(p => p?.otherFields ?? []),
      ];
      for (const field of fields) {
        if (!ExportExcelService.isExportableOtherField(field)) {
          continue;
        }

        const header = ExportExcelService.getFieldHeader(field);
        const key = TextUtil.removeAllSpaces(header);
        if (!key || seenKeys.has(key)) {
          continue;
        }

        seenKeys.add(key);
        columns.push({ header, key });
      }
    }

    return columns;
  }

  private static applyOtherFieldsToRow(row: Record<string, unknown>, fields: Field[] = []) {
    for (const field of fields) {
      if (!ExportExcelService.isExportableOtherField(field)) {
        continue;
      }

      const header = ExportExcelService.getFieldHeader(field);
      row[TextUtil.removeAllSpaces(header)] = field?.value;
    }
  }


  private addRowForEachParticipant(serialNumber: number, share: Share, template: CollectionTemplate, worksheet: ExcelJS.Worksheet) {

    let index = 0;
    const memberCount = share.participants.filter(
      p => p.roles?.indexOf(ParticipantRoles.organiser) === -1
    ).length;

    for (let participant of share.participants) {

      if (participant.roles.indexOf(ParticipantRoles.organiser) > -1) {
        continue;
      }

      // Use check-in date if available, otherwise use create time
      const dateToUse = share?.checkInData?.checkInTime || share?.createTime;
      // ponytail: group.member ids (4.a, 4.b) when share has multiple members
      const serial = memberCount > 1
        ? `${serialNumber}.${String.fromCharCode(97 + index)}`
        : (serialNumber?.toString() || '');
      
      let row: any = {
        serial,
        templateName: share?.template?.purpose || '',
        senderName: participant?.account?.name || participant?.accountSnapshot?.name || '',
        senderEmail: participant?.account?.email || participant?.accountSnapshot?.email || '',
        senderPhoneNumber: participant?.account?.phoneNumber || participant?.accountSnapshot?.phoneNumber || '',
        date: dateToUse ? this.datePipe.transform(dateToUse, 'mediumDate') : "",
        time: dateToUse ? this.datePipe.transform(dateToUse, 'shortTime') : "",
        webhookIdentifier: share?.webhookIdentifier || '',
        idName: participant.documentsAccess?.map(access => `${access?.document?.sid?.name} : ${access?.document?.documentNumberMasked || ''}`).join('\r\n'),
      };

      // Add checkout date for check-in type shares
      if (share?.template?.type === 'check-in') {
        row.checkOutDate = share?.checkInData?.checkOutTime 
          ? this.datePipe.transform(share.checkInData.checkOutTime, 'medium') 
          : '';
      }

      if (index > 0) {
        row.date = '';
        row.time = '';
        row.webhookIdentifier = '';
        row.templateName = ''
      }

      ExportExcelService.applyOtherFieldsToRow(row, share?.otherFields);
      ExportExcelService.applyOtherFieldsToRow(row, participant?.otherFields);

      worksheet.addRow(row)
      index++;
    }

  }


}
